import api from './api';

const CONFIGURATORS_URL = '/configurators';

/**
 * Configurator Service - API calls for product configurator management
 */
const configuratorService = {
  // ==========================================
  // Pipeline Status Management
  // ==========================================
  
  /**
   * Get all pipeline statuses
   */
  getPipelineStatuses: async () => {
    const response = await api.get(`${CONFIGURATORS_URL}/pipeline-statuses`);
    return response.data;
  },

  /**
   * Create a new pipeline status
   */
  createPipelineStatus: async (data) => {
    const response = await api.post(`${CONFIGURATORS_URL}/pipeline-statuses`, data);
    return response.data;
  },

  /**
   * Update a pipeline status
   */
  updatePipelineStatus: async (id, data) => {
    const response = await api.put(`${CONFIGURATORS_URL}/pipeline-statuses/${id}`, data);
    return response.data;
  },

  /**
   * Delete a pipeline status
   */
  deletePipelineStatus: async (id) => {
    const response = await api.delete(`${CONFIGURATORS_URL}/pipeline-statuses/${id}`);
    return response.data;
  },

  /**
   * Reorder pipeline statuses
   */
  reorderPipelineStatuses: async (orderedIds) => {
    const response = await api.post(`${CONFIGURATORS_URL}/pipeline-statuses/reorder`, { order: orderedIds });
    return response.data;
  },

  /**
   * Seed default pipeline statuses
   */
  seedDefaultStatuses: async () => {
    const response = await api.post(`${CONFIGURATORS_URL}/pipeline-statuses/seed`);
    return response.data;
  },

  // ==========================================
  // Analytics
  // ==========================================

  /**
   * Get dashboard analytics stats
   * @param {Object} options - Filter options
   * @param {string} [options.startDate] - ISO date string
   * @param {string} [options.endDate] - ISO date string
   * @param {number} [options.configuratorId] - Filter by configurator
   */
  getAnalyticsDashboard: async (options = {}) => {
    const params = {};
    if (options.startDate) params.start_date = options.startDate;
    if (options.endDate) params.end_date = options.endDate;
    if (options.configuratorId) params.configurator_id = options.configuratorId;
    
    const response = await api.get(`${CONFIGURATORS_URL}/analytics/dashboard`, { params });
    return response.data;
  },

  /**
   * Get popular configurator items
   * @param {Object} options - Filter options
   */
  getPopularItems: async (options = {}) => {
    const params = { limit: options.limit || 10 };
    if (options.startDate) params.start_date = options.startDate;
    if (options.endDate) params.end_date = options.endDate;
    if (options.configuratorId) params.configurator_id = options.configuratorId;
    
    const response = await api.get(`${CONFIGURATORS_URL}/analytics/popular-items`, { params });
    return response.data;
  },

  /**
   * Get orders over time data
   * @param {Object} options - Filter options
   */
  getOrdersOverTime: async (options = {}) => {
    const params = { granularity: options.granularity || 'day' };
    if (options.startDate) params.start_date = options.startDate;
    if (options.endDate) params.end_date = options.endDate;
    if (options.configuratorId) params.configurator_id = options.configuratorId;
    
    const response = await api.get(`${CONFIGURATORS_URL}/analytics/orders-over-time`, { params });
    return response.data;
  },

  /**
   * Get performance metrics per configurator
   * @param {Object} options - Filter options
   */
  getConfiguratorPerformance: async (options = {}) => {
    const params = {};
    if (options.startDate) params.start_date = options.startDate;
    if (options.endDate) params.end_date = options.endDate;
    
    const response = await api.get(`${CONFIGURATORS_URL}/analytics/performance`, { params });
    return response.data;
  },

  /**
   * Get pipeline funnel data
   * @param {Object} options - Filter options
   */
  getPipelineFunnel: async (options = {}) => {
    const params = {};
    if (options.startDate) params.start_date = options.startDate;
    if (options.endDate) params.end_date = options.endDate;
    if (options.configuratorId) params.configurator_id = options.configuratorId;
    
    const response = await api.get(`${CONFIGURATORS_URL}/analytics/pipeline-funnel`, { params });
    return response.data;
  },

  // ==========================================
  // Configurator Management
  // ==========================================

  /**
   * Get all configurators
   */
  getConfigurators: async (includeInactive = false) => {
    const response = await api.get(`${CONFIGURATORS_URL}`, {
      params: { include_inactive: includeInactive }
    });
    return response.data;
  },

  /**
   * Get a single configurator with sections and items
   */
  getConfigurator: async (id) => {
    const response = await api.get(`${CONFIGURATORS_URL}/${id}`);
    return response.data;
  },

  /**
   * Create a new configurator
   */
  createConfigurator: async (data) => {
    const response = await api.post(`${CONFIGURATORS_URL}`, data);
    return response.data;
  },

  /**
   * Update a configurator
   */
  updateConfigurator: async (id, data) => {
    const response = await api.put(`${CONFIGURATORS_URL}/${id}`, data);
    return response.data;
  },

  /**
   * Delete a configurator
   */
  deleteConfigurator: async (id) => {
    const response = await api.delete(`${CONFIGURATORS_URL}/${id}`);
    return response.data;
  },

  /**
   * Duplicate a configurator
   */
  duplicateConfigurator: async (id) => {
    const response = await api.post(`${CONFIGURATORS_URL}/${id}/duplicate`);
    return response.data;
  },

  // ==========================================
  // Section Management
  // ==========================================

  /**
   * Get sections for a configurator
   */
  getSections: async (configuratorId) => {
    const response = await api.get(`${CONFIGURATORS_URL}/${configuratorId}/sections`);
    return response.data;
  },

  /**
   * Create a section
   */
  createSection: async (configuratorId, data) => {
    const response = await api.post(`${CONFIGURATORS_URL}/${configuratorId}/sections`, data);
    return response.data;
  },

  /**
   * Update a section
   */
  updateSection: async (configuratorId, sectionId, data) => {
    const response = await api.put(`${CONFIGURATORS_URL}/${configuratorId}/sections/${sectionId}`, data);
    return response.data;
  },

  /**
   * Delete a section
   */
  deleteSection: async (configuratorId, sectionId) => {
    const response = await api.delete(`${CONFIGURATORS_URL}/${configuratorId}/sections/${sectionId}`);
    return response.data;
  },

  /**
   * Reorder sections
   */
  reorderSections: async (configuratorId, orderedIds) => {
    const response = await api.post(`${CONFIGURATORS_URL}/${configuratorId}/sections/reorder`, { order: orderedIds });
    return response.data;
  },

  // ==========================================
  // Item Management
  // ==========================================

  /**
   * Create an item
   */
  createItem: async (configuratorId, sectionId, data) => {
    const response = await api.post(`${CONFIGURATORS_URL}/${configuratorId}/sections/${sectionId}/items`, data);
    return response.data;
  },

  /**
   * Update an item
   */
  updateItem: async (configuratorId, sectionId, itemId, data) => {
    const response = await api.put(`${CONFIGURATORS_URL}/${configuratorId}/sections/${sectionId}/items/${itemId}`, data);
    return response.data;
  },

  /**
   * Delete an item
   */
  deleteItem: async (configuratorId, sectionId, itemId) => {
    const response = await api.delete(`${CONFIGURATORS_URL}/${configuratorId}/sections/${sectionId}/items/${itemId}`);
    return response.data;
  },

  /**
   * Reorder items within a section
   */
  reorderItems: async (configuratorId, sectionId, orderedIds) => {
    const response = await api.post(`${CONFIGURATORS_URL}/${configuratorId}/sections/${sectionId}/items/reorder`, { order: orderedIds });
    return response.data;
  },

  // ==========================================
  // Order Extensions
  // ==========================================

  /**
   * Get orders pipeline view (Kanban)
   */
  getOrdersPipelineView: async (configuratorId = null, includeArchived = false) => {
    const response = await api.get('/orders/pipeline-view', {
      params: {
        configurator_id: configuratorId,
        include_archived: includeArchived
      }
    });
    return response.data;
  },

  /**
   * Get orders by configurator
   */
  getOrdersByConfigurator: async (configuratorId, statusId = null, includeArchived = false) => {
    const response = await api.get(`/orders/by-configurator/${configuratorId}`, {
      params: {
        status_id: statusId,
        include_archived: includeArchived
      }
    });
    return response.data;
  },

  /**
   * Update order pipeline status
   */
  updateOrderPipelineStatus: async (orderId, statusId, notes = '') => {
    const response = await api.put(`/orders/${orderId}/pipeline-status`, {
      status_id: statusId,
      notes
    });
    return response.data;
  },

  /**
   * Get order status history
   */
  getOrderHistory: async (orderId) => {
    const response = await api.get(`/orders/${orderId}/history`);
    return response.data;
  },

  /**
   * Duplicate an order
   */
  duplicateOrder: async (orderId) => {
    const response = await api.post(`/orders/${orderId}/duplicate`);
    return response.data;
  },

  /**
   * Archive an order
   */
  archiveOrder: async (orderId) => {
    const response = await api.post(`/orders/${orderId}/archive`);
    return response.data;
  },

  /**
   * Unarchive an order
   */
  unarchiveOrder: async (orderId) => {
    const response = await api.post(`/orders/${orderId}/unarchive`);
    return response.data;
  },

  /**
   * Download order PDF
   * @param {number} orderId - Order ID
   * @param {string} template - 'auto', 'standard', or 'quote'
   * @param {string} lang - 'en', 'de', or 'sl'
   */
  downloadOrderPdf: async (orderId, template = 'auto', lang = 'en') => {
    const response = await api.get(`/orders/${orderId}/pdf`, {
      params: { template, lang },
      responseType: 'blob'
    });
    
    // Create download link
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    
    // Extract filename from response headers or use default
    const contentDisposition = response.headers['content-disposition'];
    let filename = `order_${orderId}.pdf`;
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
      if (filenameMatch) {
        filename = filenameMatch[1];
      }
    }
    
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    
    return { success: true };
  },

  /**
   * Get order PDF as blob (for preview)
   */
  getOrderPdfBlob: async (orderId, template = 'auto', lang = 'en') => {
    const response = await api.get(`/orders/${orderId}/pdf`, {
      params: { template, lang },
      responseType: 'blob'
    });
    return response.data;
  },

  // ==========================================
  // Contract Generation
  // ==========================================

  /**
   * Generate a contract from an order
   * @param {number} orderId - Order ID
   * @param {object} options - Contract generation options
   * @param {number} options.template_id - Template ID (optional, uses default if not provided)
   * @param {string} options.title - Contract title (optional)
   * @param {string} options.valid_from - Start date ISO string (optional)
   * @param {string} options.valid_until - End date ISO string (optional)
   * @param {object} options.custom_data - Additional template variables (optional)
   */
  generateOrderContract: async (orderId, options = {}) => {
    const response = await api.post(`/orders/${orderId}/contract`, options);
    return response.data;
  },

  /**
   * Get list of available contract templates
   */
  getContractTemplates: async () => {
    const response = await api.get('/contracts/templates');
    return response.data;
  },

  /**
   * Download a generated contract
   * @param {number} contractId - Contract ID
   */
  downloadContract: async (contractId) => {
    const response = await api.get(`/contracts/${contractId}/download`, {
      responseType: 'blob'
    });
    
    // Create download link
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    
    // Extract filename from response headers or use default
    const contentDisposition = response.headers['content-disposition'];
    let filename = `contract_${contractId}.docx`;
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
      if (filenameMatch) {
        filename = filenameMatch[1];
      }
    }
    
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    
    return { success: true };
  },

  // ==========================================
  // Email Notifications
  // ==========================================

  /**
   * Send an email for an order
   * @param {number} orderId - Order ID
   * @param {Object} options - Email options
   * @param {string} options.type - Email type: 'confirmation', 'status_update', 'custom'
   * @param {string} options.recipient - Recipient: 'customer', 'admin', 'custom'
   * @param {string} [options.customEmail] - Custom email address (if recipient is 'custom')
   * @param {string} [options.subject] - Custom email subject (for 'custom' type)
   * @param {string} [options.message] - Custom message (for 'custom' type)
   */
  sendOrderEmail: async (orderId, options) => {
    const response = await api.post(`/orders/${orderId}/send-email`, {
      type: options.type,
      recipient: options.recipient,
      custom_email: options.customEmail,
      subject: options.subject,
      message: options.message
    });
    return response.data;
  },

  // ==========================================
  // Email Templates
  // ==========================================

  /**
   * Get all email templates
   * @param {Object} options - Filter options
   * @param {string} [options.type] - Filter by template type
   * @param {number} [options.configuratorId] - Filter by configurator
   */
  getEmailTemplates: async (options = {}) => {
    const params = {};
    if (options.type) params.type = options.type;
    if (options.configuratorId) params.configurator_id = options.configuratorId;
    
    const response = await api.get(`${CONFIGURATORS_URL}/email-templates`, { params });
    return response.data;
  },

  /**
   * Get a specific email template
   * @param {number} templateId - Template ID
   */
  getEmailTemplate: async (templateId) => {
    const response = await api.get(`${CONFIGURATORS_URL}/email-templates/${templateId}`);
    return response.data;
  },

  /**
   * Create a new email template
   * @param {Object} data - Template data
   */
  createEmailTemplate: async (data) => {
    const response = await api.post(`${CONFIGURATORS_URL}/email-templates`, data);
    return response.data;
  },

  /**
   * Update an email template
   * @param {number} templateId - Template ID
   * @param {Object} data - Updated template data
   */
  updateEmailTemplate: async (templateId, data) => {
    const response = await api.put(`${CONFIGURATORS_URL}/email-templates/${templateId}`, data);
    return response.data;
  },

  /**
   * Delete an email template
   * @param {number} templateId - Template ID
   */
  deleteEmailTemplate: async (templateId) => {
    const response = await api.delete(`${CONFIGURATORS_URL}/email-templates/${templateId}`);
    return response.data;
  },

  /**
   * Preview an email template with sample data
   * @param {number} templateId - Template ID
   * @param {Object} [sampleData] - Optional custom sample data
   */
  previewEmailTemplate: async (templateId, sampleData = {}) => {
    const response = await api.post(`${CONFIGURATORS_URL}/email-templates/${templateId}/preview`, sampleData);
    return response.data;
  },

  /**
   * Seed default email templates
   */
  seedEmailTemplates: async () => {
    const response = await api.post(`${CONFIGURATORS_URL}/email-templates/seed`);
    return response.data;
  }
};

export default configuratorService;
